// LLM generated - Model Claude 4 Sonnet
import { forwardRef, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from 'react';

import ChatBubble from '../ChatBubble/ChatBubble';
import {
  ChatBubbleSkeleton,
  generateChatSkeletonPattern,
} from '../ChatBubbleSkeleton/ChatBubbleSkeleton';
import type { ChatMessagesListProps } from './types';
import useStyles from './ChatMessagesList.styles';

const ChatMessagesList = forwardRef<HTMLDivElement, ChatMessagesListProps>(
  (
    {
      messages,
      currentUserHandle,
      onReportClick,
      onDeleteClick,
      onReactionSelect,
      onReactionRemove,
      availableReactions = [],
      isLoadingReactions = false,
      getUserReactions,
      loaderRef,
      scrollContainerRef,
      hasNextPage,
      isFetchingNextPage,
      isLoading = false,
    },
    ref
  ) => {
    const { classes } = useStyles();
    const messagesListRef = useRef<HTMLDivElement>(null);
    const isLoadingPrevious = useRef(false);
    const wasAtBottom = useRef(true);
    // Track scroll height before new messages are rendered to preserve scroll position
    const previousScrollHeightRef = useRef<number>(0);
    const previousMessageCountRef = useRef<number>(0);

    const isAtBottom = () => {
      if (!messagesListRef.current) return false;

      const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
      return scrollTop + clientHeight >= scrollHeight - 10;
    };

    const handleScroll = useCallback(() => {
      wasAtBottom.current = isAtBottom();
    }, []);

    // Capture scroll height BEFORE new messages render (when loading previous messages)
    // This runs synchronously before the DOM updates
    useLayoutEffect(() => {
      if (isFetchingNextPage && messagesListRef.current) {
        previousScrollHeightRef.current = messagesListRef.current.scrollHeight;
        previousMessageCountRef.current = messages.length;
        isLoadingPrevious.current = true;
      }
    }, [isFetchingNextPage, messages.length]);

    // Restore scroll position AFTER new messages render (synchronously before paint)
    // This prevents the visual "jump" when older messages are prepended
    useLayoutEffect(() => {
      if (!messagesListRef.current) return;

      // Check if we just loaded previous (older) messages
      const loadedPreviousMessages =
        isLoadingPrevious.current &&
        messages.length > previousMessageCountRef.current &&
        previousScrollHeightRef.current > 0;

      if (loadedPreviousMessages) {
        // Calculate how much the scroll height increased
        const newScrollHeight = messagesListRef.current.scrollHeight;
        const scrollDiff = newScrollHeight - previousScrollHeightRef.current;

        // Adjust scroll position to maintain visual position
        // The new messages were added at the top, so we need to scroll down by the difference
        messagesListRef.current.scrollTop += scrollDiff;

        // Reset tracking refs
        previousScrollHeightRef.current = 0;
        previousMessageCountRef.current = messages.length;
        isLoadingPrevious.current = false;
      } else if (!isLoadingPrevious.current) {
        // New message arrived at bottom (not loading previous)
        const currentlyAtBottom = isAtBottom();

        // Auto-scroll if user was at the bottom before new messages arrived
        if (wasAtBottom.current || currentlyAtBottom) {
          messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
        }
      }
    }, [messages]);

    // Initialize wasAtBottom on first render
    useEffect(() => {
      if (messagesListRef.current) {
        wasAtBottom.current = isAtBottom();
      }
    }, []);

    // Generate skeleton pattern once and memoize it to prevent re-rendering jumble
    const skeletonPattern = useMemo(() => generateChatSkeletonPattern(), []);

    // Memoize reversed messages to avoid creating new array on every render
    const reversedMessages = useMemo(() => [...messages].reverse(), [messages]);

    // Memoize ref callback to avoid re-creating on every render
    const setMessagesListRef = useCallback(
      (node: HTMLDivElement | null) => {
        messagesListRef.current = node;
        if (scrollContainerRef && 'current' in scrollContainerRef) {
          (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [scrollContainerRef]
    );

    // Show skeleton loading state during initial load
    if (isLoading && messages.length === 0) {
      return (
        <div ref={ref} className={classes.container}>
          <div ref={setMessagesListRef} className={classes.messagesList}>
            {skeletonPattern.map((pattern) => (
              <div key={pattern.id} className={classes.messageItem}>
                <ChatBubbleSkeleton
                  variant={pattern.variant}
                  showMultipleLines={pattern.showMultipleLines}
                />
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (messages.length === 0) {
      return null;
    }

    return (
      <div ref={ref} className={classes.container}>
        <div ref={setMessagesListRef} className={classes.messagesList} onScroll={handleScroll}>
          {/* Loader for infinite scroll - positioned at TOP for loading previous messages */}
          {hasNextPage && (
            <div ref={loaderRef} className={classes.loader}>
              {isFetchingNextPage ? 'Loading previous messages...' : ''}
            </div>
          )}

          {reversedMessages.map((message) => (
            <div key={message.id} className={classes.messageItem}>
              <ChatBubble
                message={{
                  ...message,
                  variant:
                    message.variant ||
                    (message.isCurrentUser || message.handle === currentUserHandle
                      ? 'current'
                      : 'other'),
                }}
                onReportClick={onReportClick}
                onDeleteClick={onDeleteClick}
                onReactionSelect={onReactionSelect}
                onReactionRemove={onReactionRemove}
                availableReactions={availableReactions}
                isLoadingReactions={isLoadingReactions}
                currentUserHandle={currentUserHandle}
                userReactions={getUserReactions ? getUserReactions(message.id) : []}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
);

ChatMessagesList.displayName = 'ChatMessagesList';

export default ChatMessagesList;
